import { useState } from "react";
import {
  Alert,
  Anchor,
  Button,
  Group,
  Loader,
  NumberInput,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { ApiError } from "../api/client";
import { useMayRunShows } from "../auth/roles";
import { catalogue, type Settings, type SyncRun } from "../catalogue/api";
import { SyncProduct } from "../catalogue/SyncProduct";
import { useCatalogue } from "../catalogue/useCatalogue";
import styles from "../catalogue/Catalogue.module.scss";
import { KlaviyoSettings } from "./KlaviyoSettings";
import { PixelSettings } from "./PixelSettings";

/**
 * The platform's settings and the catalogue's health (M1.13, D5 and D6).
 *
 * Four numbers that govern every show — the minimum stock a product needs to be featured, the
 * share of stock a featured lot offers by default, the shipping fee, and what the break-even bid
 * allows for packing and postage — and beside them the one thing an operator
 * needs to know about the catalogue copy: when it was last rewritten from Shopify, whether that
 * worked, and how much the webhooks had missed (`drift`). *Resync* is here for the day the answer
 * to the last question is *a lot*.
 *
 * Host only: the routes refuse a moderator, and the page says so rather than offering a form.
 */
export function SettingsPage() {
  const { t } = useTranslation();
  const mayRun = useMayRunShows();
  const { loaded, settings, runs, entries, refresh } = useCatalogue();

  if (!mayRun) {
    return (
      <Alert color="gray" variant="light" radius="md">
        {t("settings.hostOnly")}
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      <Anchor component={Link} to="/" size="xs">
        {t("shows.backToList")}
      </Anchor>

      {!loaded || !settings ? (
        <Group justify="center" py="xl">
          <Loader size="sm" color="ink" />
        </Group>
      ) : (
        <>
          {/*
            Each card keyed on its own values only, so saving one does not remount the other and
            throw away an edit somebody had not saved yet.
          */}
          <SettingsForm
            key={JSON.stringify({ ...settings, metaPixelId: undefined })}
            settings={settings}
            onSaved={refresh}
          />
          <PixelSettings
            key={settings.metaPixelId}
            metaPixelId={settings.metaPixelId}
            onSaved={refresh}
          />
        </>
      )}

      <KlaviyoSettings />

      <Paper withBorder radius="lg" p="lg" className={styles.card}>
        <Stack gap="sm">
          <Text fw={600}>{t("settings.catalogueHeading")}</Text>
          <Text size="sm" c="dimmed">
            {t("settings.catalogueSize", { count: entries.length })}
          </Text>
          <RunLine label={t("settings.lastFull")} run={runs?.full ?? null} />
          <RunLine label={t("settings.lastStock")} run={runs?.stock ?? null} />
          <Resync onStarted={refresh} />
          <SyncProduct onSynced={refresh} />
        </Stack>
      </Paper>
    </Stack>
  );
}

function SettingsForm({
  settings,
  onSaved,
}: {
  settings: Settings;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [minimum, setMinimum] = useState<number>(settings.minFeatureStock);
  const [share, setShare] = useState<number>(settings.featureSharePercent);
  /*
    Held as the number the input shows, and sent as the two-place string every price travels as.
    The input refuses anything below a penny, and the server refuses zero whatever arrives.
  */
  const [shipping, setShipping] = useState<number | "">(Number(settings.shippingFee));
  // The same, except that zero is a legal answer.
  const [addition, setAddition] = useState<number | "">(
    Number(settings.breakEvenAddition),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [saved, setSaved] = useState(false);

  async function save() {
    setBusy(true);
    setError(undefined);
    setSaved(false);
    try {
      await catalogue.saveSettings({
        minFeatureStock: minimum,
        featureSharePercent: share,
        shippingFee: shipping === "" ? "" : shipping.toFixed(2),
        breakEvenAddition: addition === "" ? "" : addition.toFixed(2),
      });
      setSaved(true);
      await onSaved();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t("shows.unreachable"));
    } finally {
      setBusy(false);
    }
  }

  const unchanged =
    minimum === settings.minFeatureStock &&
    share === settings.featureSharePercent &&
    shipping === Number(settings.shippingFee) &&
    addition === Number(settings.breakEvenAddition);

  return (
    <Paper withBorder radius="lg" p="lg" className={styles.card}>
      <Stack gap="sm">
        <Text fw={600}>{t("settings.heading")}</Text>
        <Text size="sm" c="dimmed">
          {t("settings.help")}
        </Text>
        <Group gap="md" align="flex-end" wrap="wrap">
          <NumberInput
            label={t("settings.minimumLabel")}
            description={t("settings.minimumHelp")}
            value={minimum}
            min={1}
            max={1000}
            allowDecimal={false}
            onChange={(value) => setMinimum(Number(value) || 0)}
            disabled={busy}
          />
          <NumberInput
            label={t("settings.shareLabel")}
            description={t("settings.shareHelp")}
            value={share}
            min={1}
            max={99}
            suffix="%"
            allowDecimal={false}
            onChange={(value) => setShare(Number(value) || 0)}
            disabled={busy}
          />
          {/*
            Spec §15.2's flat fee, charged once per customer per show. Never zero — the server
            refuses it — and copied onto a show when it is published, which the help text says,
            because a host changing it mid-show would otherwise expect tonight's show to follow.
          */}
          <NumberInput
            label={t("settings.shippingLabel")}
            description={t("settings.shippingHelp")}
            value={shipping}
            min={0.01}
            max={100}
            prefix="£"
            decimalScale={2}
            fixedDecimalScale
            allowNegative={false}
            onChange={(value) => setShipping(value === "" ? "" : Number(value))}
            disabled={busy}
          />
          {/*
            `R-LOT-31` — added to every break-even bid the panel shows, for packing and postage.
            Changes no price and nothing a customer sees, so it applies on the next load.
          */}
          <NumberInput
            label={t("settings.breakEvenLabel")}
            description={t("settings.breakEvenHelp")}
            value={addition}
            min={0}
            max={100}
            prefix="£"
            decimalScale={2}
            fixedDecimalScale
            allowNegative={false}
            onChange={(value) => setAddition(value === "" ? "" : Number(value))}
            disabled={busy}
          />
          <Button
            onClick={() => void save()}
            loading={busy}
            disabled={unchanged || shipping === "" || shipping <= 0 || addition === ""}
          >
            {t("settings.save")}
          </Button>
        </Group>
        {saved ? (
          <Text size="xs" c="green">
            {t("settings.saved")}
          </Text>
        ) : null}
        {error ? (
          <Alert color="red" variant="light" radius="md">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}

function RunLine({ label, run }: { label: string; run: SyncRun | null }) {
  const { t } = useTranslation();
  if (!run) {
    return (
      <Text size="sm">
        {label}: {t("settings.never")}
      </Text>
    );
  }
  const when = new Date(run.startedAt).toLocaleString("en-GB");
  return (
    <Stack gap={0}>
      <Text size="sm">
        {label}: {t(`settings.runState.${run.state}`)} · {when} ·{" "}
        {t(`settings.trigger.${run.trigger}`)}
      </Text>
      <Text size="xs" c={run.state === "failed" ? "red" : "dimmed"}>
        {run.state === "failed"
          ? (run.error ?? "")
          : t(run.kind === "full" ? "settings.fullDetail" : "settings.stockDetail", {
              products: run.products ?? 0,
              variants: run.variants ?? 0,
              drift: run.drift ?? 0,
              removed: run.removed ?? 0,
              seconds: Math.round((run.durationMs ?? 0) / 1000),
            })}
      </Text>
    </Stack>
  );
}

function Resync({ onStarted }: { onStarted: () => Promise<void> }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | undefined>(undefined);

  return (
    <Group gap="sm">
      <Button
        variant="default"
        size="xs"
        loading={busy}
        onClick={() => {
          setBusy(true);
          setNote(undefined);
          void catalogue
            .resync()
            .then(() => setNote(t("settings.resyncStarted")))
            .catch((failure: unknown) =>
              setNote(
                failure instanceof ApiError ? failure.message : t("shows.unreachable"),
              ),
            )
            .finally(() => {
              setBusy(false);
              void onStarted();
            });
        }}
      >
        {t("settings.resync")}
      </Button>
      {note ? (
        <Text size="xs" c="dimmed">
          {note}
        </Text>
      ) : null}
    </Group>
  );
}
